import { useState, useEffect, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Loader2, CreditCard, Plus, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import { AccountShell } from "../../components/account/AccountShell";
import { useAuth } from "../../hooks/useAuth";
import {
  getPaymentMethods,
  deletePaymentMethod,
  setDefaultPaymentMethod,
  createSetupIntent,
} from "../../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const BRAND_COLORS = {
  Visa: "bg-[#1a1f71]",
  Mastercard: "bg-[#eb001b]",
  Amex: "bg-[#2e77bc]",
  Discover: "bg-[#ff6000]",
  Card: "bg-[#0F0F0F]",
};

const CARD_STYLE = {
  style: {
    base: { fontSize: "15px", color: "#0F0F0F", "::placeholder": { color: "#9CA3AF" } },
    invalid: { color: "#dc2626" },
  },
};

const AddCardForm = ({ onDone }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    try {
      const { client_secret } = await createSetupIntent();
      const result = await stripe.confirmCardSetup(client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { name: name || undefined },
        },
      });
      if (result.error) {
        toast.error(result.error.message || "Could not save card.");
        return;
      }
      toast.success("Card added");
      onDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save card.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-[#374151]">Cardholder name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name on card"
          className="mt-1.5 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37]"
          data-testid="card-name-input"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-[#374151]">Card details</label>
        <div className="mt-1.5 rounded-xl border border-gray-200 px-4 py-3.5" data-testid="card-element">
          <CardElement options={CARD_STYLE} />
        </div>
        <p className="text-xs text-[#9CA3AF] mt-2">Test card: 4242 4242 4242 4242 · any future date · any CVC.</p>
      </div>
      <button
        onClick={submit}
        disabled={busy || !stripe}
        className="mt-1 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0F0F0F] text-white py-3 text-sm font-semibold hover:bg-[#1c1c1c] transition-colors disabled:opacity-60"
        data-testid="save-card-btn"
      >
        {busy ? <Loader2 className="animate-spin" size={15} /> : "Save Card"}
      </button>
    </div>
  );
};

export default function AccountPaymentMethods() {
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(() => {
    if (!user) return;
    setLoading(true);
    getPaymentMethods()
      .then(setCards)
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const onCardAdded = (ok) => {
    setModalOpen(false);
    if (ok) load();
  };

  const makeDefault = async (card) => {
    try {
      await setDefaultPaymentMethod(card.id);
      toast.success("Default card updated");
      load();
    } catch {
      toast.error("Could not set default.");
    }
  };

  const remove = async (card) => {
    try {
      await deletePaymentMethod(card.id);
      toast.success("Card removed");
      load();
    } catch {
      toast.error("Could not remove card.");
    }
  };

  return (
    <AccountShell title="Payment Methods" subtitle="Manage the cards on your account." testid="account-payment-methods-page">
      <div className="flex justify-end mb-5">
        <button
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-[#0F0F0F] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#1c1c1c] transition-colors"
          data-testid="add-payment-method-btn"
        >
          <Plus size={16} /> Add Payment Method
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#687280]">
          <Loader2 className="animate-spin" size={22} />
        </div>
      ) : cards.length === 0 ? (
        <div className="bg-[#F7F7F7] rounded-2xl p-10 text-center" data-testid="no-cards-state">
          <div className="w-12 h-12 rounded-xl bg-white text-[#D4AF37] flex items-center justify-center mx-auto mb-4">
            <CreditCard size={22} />
          </div>
          <p className="font-display font-semibold text-[#0F0F0F]">No payment methods</p>
          <p className="text-sm text-[#687280] mt-1">Add a card to keep it securely on file with Stripe.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {cards.map((card) => (
            <div
              key={card.id}
              className="bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
              data-testid={`payment-method-card-${card.id}`}
            >
              <div className="flex items-start justify-between">
                <div className={`w-12 h-8 rounded-md ${BRAND_COLORS[card.brand] || BRAND_COLORS.Card} text-white text-[10px] font-bold flex items-center justify-center`}>
                  {card.brand.toUpperCase().slice(0, 4)}
                </div>
                {card.is_default && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#D4AF37]/15 text-[#8a6d1a] px-2.5 py-0.5 text-xs font-semibold">
                    <Star size={11} /> Default
                  </span>
                )}
              </div>
              <p className="font-display text-lg font-bold text-[#0F0F0F] mt-4 tracking-wider">
                {card.brand} •••• {card.last4}
              </p>
              <p className="text-sm text-[#687280] mt-1">
                Expires {String(card.exp_month).padStart(2, "0")}/{String(card.exp_year).slice(-2)}
                {card.name ? ` · ${card.name}` : ""}
              </p>
              <div className="flex items-center gap-2 mt-5">
                {!card.is_default && (
                  <button
                    onClick={() => makeDefault(card)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3.5 py-1.5 text-xs font-semibold text-[#0F0F0F] hover:border-[#D4AF37] transition-colors"
                    data-testid={`set-default-${card.id}`}
                  >
                    <Star size={13} /> Set Default
                  </button>
                )}
                <button
                  onClick={() => remove(card)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-red-200 text-red-600 px-3.5 py-1.5 text-xs font-semibold hover:bg-red-50 transition-colors"
                  data-testid={`remove-card-${card.id}`}
                >
                  <Trash2 size={13} /> Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="payment-method-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Add Payment Method</DialogTitle>
            <DialogDescription>Your card is saved securely by Stripe. We never store card numbers.</DialogDescription>
          </DialogHeader>
          {modalOpen && (
            <Elements stripe={stripePromise}>
              <AddCardForm onDone={onCardAdded} />
            </Elements>
          )}
        </DialogContent>
      </Dialog>
    </AccountShell>
  );
}
